import React, { useState, useEffect } from 'react';
import { Mission, MissionStatus } from '../../types';
import { formatDistanceToNow } from 'date-fns';

interface ElapsedTimeTrackerProps {
  mission: Mission;
}

interface TimeSegment {
  label: string;
  startTime: Date | null;
  endTime: Date | null;
  isActive: boolean;
  warning?: boolean;
}

const ElapsedTimeTracker: React.FC<ElapsedTimeTrackerProps> = ({ mission }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second for active segments
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Calculate time segments based on mission status
  const getTimeSegments = (): TimeSegment[] => {
    const segments: TimeSegment[] = [];
    const missionStart = new Date(mission.dateCreated);

    // Determine mission end time: either dateClosed or timeMissionComplete
    const missionEndTime = mission.dateClosed
      ? new Date(mission.dateClosed)
      : mission.timeMissionComplete
        ? new Date(mission.timeMissionComplete)
        : null;

    // ALWAYS show: Total Mission Time
    segments.push({
      label: 'Total Mission Time',
      startTime: missionStart,
      endTime: missionEndTime,
      isActive: !missionEndTime,
      warning: false,
    });

    // Show "Enroute to Scene" - only while actually enroute (hide once on scene)
    if (mission.status === MissionStatus.ENROUTE_TO_SCENE) {
      segments.push({
        label: 'Enroute to Scene',
        startTime: mission.timeEnrouteToScene ? new Date(mission.timeEnrouteToScene) : missionStart,
        endTime: null,
        isActive: true,
        warning: false,
      });
    }

    // Show "On Scene" - only while on scene (hide when transporting or complete)
    const isOnScene = mission.status === MissionStatus.ON_SCENE || mission.status === MissionStatus.PATIENT_CONTACT;

    if (isOnScene) {
      segments.push({
        label: 'On Scene',
        startTime: mission.timeOnScene ? new Date(mission.timeOnScene) : missionStart,
        endTime: null,
        isActive: true,
        warning: false,
      });
    }

    return segments;
  };

  const formatElapsedTime = (startTime: Date | null, endTime: Date | null): string => {
    if (!startTime) return '--:--:--';

    // For closed missions, calculate against dateClosed/timeMissionComplete, not now
    const closedStatuses = [MissionStatus.CLOSED, MissionStatus.MISSION_COMPLETE, MissionStatus.CANCELLED];
    const isClosed = closedStatuses.includes(mission.status as MissionStatus);
    let end = endTime || currentTime;
    if (isClosed && !endTime) {
      const closedTime = mission.dateClosed || mission.timeMissionComplete;
      end = closedTime ? new Date(closedTime) : currentTime;
    }

    const diffMs = end.getTime() - startTime.getTime();

    // Handle negative time (data issue)
    if (diffMs < 0) {
      return '--:--:--';
    }

    const totalDays = diffMs / (1000 * 60 * 60 * 24);

    // If > 365 days, show start/end dates only
    if (totalDays > 365) {
      const startStr = startTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${startStr} - ${endStr}`;
    }

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    // If >= 24 hours, show Xd Xh Xm format
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}d ${remainingHours}h ${minutes}m`;
    }

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const segments = getTimeSegments();

  return (
    <div className="bg-white rounded-lg shadow p-3">
      <div className="space-y-2">
        {segments.map((segment, index) => (
          <div
            key={index}
            className={`p-2 rounded border ${
              segment.isActive
                ? 'bg-blue-50 border-blue-300'
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex justify-between items-center">
              <span className={`text-sm font-medium ${
                segment.isActive ? 'text-blue-900' : 'text-gray-700'
              }`}>
                {segment.label}
              </span>
              <div className="flex items-center space-x-2">
                <span className={`text-xl font-mono ${
                  segment.isActive ? 'text-blue-600' : 'text-gray-900'
                }`}>
                  {formatElapsedTime(segment.startTime, segment.endTime)}
                </span>
                {segment.isActive && (
                  <span className="flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                  </span>
                )}
              </div>
            </div>
            {segment.startTime && (
              <div className="text-xs text-gray-500 mt-0.5">
                Started: {formatDistanceToNow(segment.startTime, { addSuffix: true })}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-2 text-xs text-gray-500">
        <p>* Times update in real-time. Mission status changes are tracked automatically.</p>
      </div>
    </div>
  );
};

export default ElapsedTimeTracker;
